import React, { useState, useEffect, useCallback } from 'react'
import { Auth } from 'aws-amplify'
import {
    isAdmin,
    isMultipleOrganization,
    isPlatformAdmin,
    getUserData,
} from '../../../Utils/ServerUtil'
import { FTCheckbox } from '../../FTCheckbox'
import Menu from '@material-ui/core/Menu'
import { Divider, TextField, Autocomplete, Box } from '@mui/material'
import eventCapture from '../../../eventList'
import { posthogCapture } from '../../../PostHogFunction'
import { makeStyles } from '@material-ui/core/styles'
import NotFeature from '../../../fhg/security/NotFeature'
import { CUSTOMER_ENV } from '../../../Utils/ServerUtil'
import { FTButtonIconText } from '../../buttons'
import { FTFilterIcon } from '../../icons'
import { useEmployeesStore } from '../../zustandState/ZustandStore'
import { useMenuStore } from '../../zustandState/ZustandStore'
import config from '../../../config/runtime'
import { logger } from '../../../config/logger'

const useStyles = makeStyles((theme) => ({
    mouseTracker: {
        '&:focus': {
            outline: 'none',
        },
    },
    menuContent: {
        padding: '16px',
        minWidth: '350px',
    },
    autocompleteWrapper: {
        marginBottom: '12px',
        '& .MuiOutlinedInput-root': {
            '&:hover fieldset': {
                borderColor: '#4a287d',
            },
            '&.Mui-focused fieldset': {
                borderColor: '#4a287d',
            },
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#4a287d',
        },
    },
    optionContent: {
        display: 'flex',
        flexDirection: 'column',
    },
    optionName: {
        color: theme.palette.text.primary,
        lineHeight: 1.3,
    },
    optionEmail: {
        color: theme.palette.text.secondary,
        fontSize: '0.875rem',
        lineHeight: 1.3,
    },
}))

const getEmployeeKey = (employee) =>
    employee?.user_id || employee?.email || employee?.name || ''

function EmployeesFilterMenu() {
    const classes = useStyles()
    const { setSelectedEmployeesState } = useEmployeesStore()
    const { setMenuClosedState } = useMenuStore()

    const [employees, setEmployees] = useState([])
    const [selectedEmployees, setSelectedEmployees] = useState([])
    const [selectAllEmployees, setSelectAllEmployees] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)
    const [anchorEl, setAnchorEl] = useState(null)

    useEffect(() => {
        fetchEmployees()
    }, [])

    useEffect(() => {
        setSelectedEmployeesState(
            [...new Set(selectedEmployees.map((employee) => employee?.name))].filter(
                Boolean
            )
        )
    }, [selectedEmployees, setSelectedEmployeesState])

    useEffect(() => {
        setMenuClosedState(!menuOpen)
    }, [menuOpen, setMenuClosedState])

    const handleEmployeeMenuToggle = (event) => {
        if (menuOpen) {
            setAnchorEl(null)
        } else {
            setAnchorEl(event.currentTarget)
        }
        setMenuOpen(!menuOpen)
    }

    const handleSelectAllChange = useCallback(() => {
        const newSelectAllEmployees = !selectAllEmployees

        if (newSelectAllEmployees) {
            setSelectedEmployees([...employees])
        } else {
            setSelectedEmployees([])
        }

        setSelectAllEmployees(newSelectAllEmployees)
    }, [selectAllEmployees, employees])

    const fetchEmployees = async () => {
        const userData = getUserData()
        if (!userData || !userData.isPlatformAdmin) {
            return
        }

        let jwtToken

        try {
            const session = await Auth.currentSession()
            jwtToken = session.idToken.jwtToken
        } catch (e) {
            try {
                const session = await Auth.currentSession()
                jwtToken = session.idToken.jwtToken
            } catch (e) {
                // Error refreshing token
                return // Exit if we can't get a token
            }
        }

        const headers = {
            authorization: jwtToken,
            accept: 'application/json',
        }

        if (isAdmin() || isMultipleOrganization()) {
            const organization = localStorage.getItem(
                'hw.' + window.btoa('organization')
            )
            if (organization && window.atob(organization)) {
                headers['HW-Organization'] = window.atob(organization)
                    ? window.atob(organization)
                    : 'f376c401-6295-4038-a146-e8b52eef6385'
            }
        }

        const options = {
            method: 'GET',
            headers: headers,
        }

        const url = `${config.API_ENDPOINT_V3}/employees`

        try {
            const response = await fetch(url, options)

            if (response.ok) {
                const employeeData = await response.json()
                const employeesList = Array.isArray(employeeData)
                    ? employeeData
                    : []
                setEmployees(employeesList)
                logger.info('filter_employees_loaded', {
                    count: employeesList.length,
                })
            } else {
                logger.error('filter_employees_load_failure', {
                    status: response.status,
                })
            }
        } catch (error) {
            logger.error('filter_employees_load_failure', {
                message_code: error.message,
            })
        }
    }

    return (
        <div>
            <NotFeature feature={CUSTOMER_ENV}>
                <FTButtonIconText
                    id="employee-filter-button"
                    title="evalDashboard.employees.column"
                    buttonAction={handleEmployeeMenuToggle}
                    icon={
                        <FTFilterIcon
                            activeFilter={
                                selectedEmployees.length > 0 &&
                                selectedEmployees.length <
                                    employees.length
                            }
                        />
                    }
                />
                <Menu
                    anchorEl={anchorEl}
                    open={menuOpen}
                    onClose={handleEmployeeMenuToggle}
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    getContentAnchorEl={null}
                >
                    <div className={classes.menuContent}>
                        <div className={classes.autocompleteWrapper}>
                            <Autocomplete
                                multiple
                                id="employee-autocomplete"
                                options={employees}
                                value={selectedEmployees}
                                onChange={(event, newValue) => {
                                    posthogCapture(
                                        eventCapture.FILTERED_BY_EMPLOYEES
                                    )
                                    setSelectedEmployees(newValue)
                                    logger.info('filter_employees_apply', {
                                        count: newValue.length,
                                    })

                                    if (
                                        newValue.length === employees.length
                                    ) {
                                        setSelectAllEmployees(true)
                                    } else {
                                        setSelectAllEmployees(false)
                                    }
                                }}
                                disableCloseOnSelect
                                getOptionLabel={(option) =>
                                    option?.name || ''
                                }
                                isOptionEqualToValue={(option, value) =>
                                    getEmployeeKey(option) ===
                                    getEmployeeKey(value)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Search and select employees"
                                        placeholder="Type to search..."
                                        size="small"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <li
                                        {...props}
                                        key={getEmployeeKey(option)}
                                    >
                                        <FTCheckbox
                                            checked={selectedEmployees.some(
                                                (employee) =>
                                                    getEmployeeKey(employee) ===
                                                    getEmployeeKey(option)
                                            )}
                                            style={{
                                                marginRight: 8,
                                                color: selectedEmployees.some(
                                                    (employee) =>
                                                        getEmployeeKey(
                                                            employee
                                                        ) ===
                                                        getEmployeeKey(option)
                                                )
                                                    ? '#4a287d'
                                                    : undefined,
                                            }}
                                            sx={
                                                selectedEmployees.some(
                                                    (employee) =>
                                                        getEmployeeKey(
                                                            employee
                                                        ) ===
                                                        getEmployeeKey(option)
                                                )
                                                    ? {
                                                          '& .MuiSvgIcon-root':
                                                              {
                                                                  color: '#4a287d',
                                                              },
                                                          '&.Mui-checked': {
                                                              color: '#4a287d',
                                                          },
                                                      }
                                                    : {}
                                            }
                                        />
                                        <div className={classes.optionContent}>
                                            <span className={classes.optionName}>
                                                {option.name || 'Unknown'}
                                            </span>
                                            <span className={classes.optionEmail}>
                                                {option.email || 'No email'}
                                            </span>
                                        </div>
                                    </li>
                                )}
                                style={{ width: '100%' }}
                            />
                        </div>

                        <Divider />

                        <Box sx={{ mt: 1, mb: 1 }}>
                            <FTCheckbox
                                key="Select All"
                                value="Select All"
                                onClick={handleSelectAllChange}
                                checked={selectAllEmployees}
                                style={{
                                    color: selectAllEmployees
                                        ? '#4a287d'
                                        : undefined,
                                }}
                                sx={{
                                    '&.Mui-checked': {
                                        color: '#4a287d',
                                    },
                                    '& .MuiSvgIcon-root': {
                                        color: '#4a287d',
                                    },
                                }}
                            />
                        </Box>
                    </div>
                </Menu>
            </NotFeature>
        </div>
    )
}

export default EmployeesFilterMenu
